import AuthenticationType from '@alltrails/analytics/enums/AuthenticationType';
import DeferredDeepLinkType from '@alltrails/analytics/enums/DeferredDeepLinkType';
import logSignUpSuccess from '@alltrails/analytics/events/logSignUpSuccess';
import logLogInSuccess from '@alltrails/analytics/events/logLogInSuccess';
import { getAmplitudeInstance } from '@alltrails/analytics/amplitudeHelpers';
import logLogInFailed from '@alltrails/analytics/events/logLogInFailed';
import logSignUpFailed from '@alltrails/analytics/events/logSignUpFailed';
import { LegacyAPIError } from 'types/Errors';
import { ServerCommunicationUtil } from './server_communication_util';

// Please see OAuthHandlers for handling apple, facebook, google, etc. login/signups

// TODO: Eventually consolidate some of this event tracking logic on backend and
// move this tracking there instead to be more accurate, improve client performance,
// and avoid GDPR cookie gotchas.
// Note: Sending user_ids client-side is still important for merging web users with mobile users.
const authSuccessCallback = (data: any, authType: AuthenticationType, isCollabListInvite?: boolean) => {
  const { is_sign_up, user } = data;
  if (is_sign_up) {
    // TODO: require as argument + pass-in user.id to logSignupSuccess instead of setting outside
    getAmplitudeInstance().setUserId(user?.id);
    if (isCollabListInvite) {
      logSignUpSuccess({ authentication_type: authType, deferred_deep_link_type: DeferredDeepLinkType.CollaborativeList });
    } else {
      logSignUpSuccess({ authentication_type: authType });
    }

    // GA
    if (typeof gtag !== 'undefined') {
      // Set GA user id
      // https://developers.google.com/analytics/devguides/collection/gtagjs/cookies-user-id
      gtag('config', window.ga4_measure_id, {
        user_id: user.id
      });
      // GA UA
      let pagePart;
      if (authType === AuthenticationType.Email) {
        pagePart = '/';
      } else if (authType === AuthenticationType.GoogleOneTap) {
        pagePart = '/google/';
      } else {
        pagePart = `/${authType}/`;
      }
      gtag('event', 'page_view', { page_path: `/register${pagePart}success` });
      // GA4
      // https://developers.google.com/analytics/devguides/collection/ga4/reference/events#sign_up
      gtag('event', 'sign_up', { method: authType });
    }
    // FB Pixel
    // https://developers.facebook.com/docs/meta-pixel/reference#:~:text=ADD_TO_WISHLIST-,CompleteRegistration,-When%20a%20registration
    // Check for presence of fbq in case of content blockers
    if (typeof fbq !== 'undefined') {
      fbq('init', window.fb_pixel_id, {
        external_id: user.id
      });
      fbq('track', 'CompleteRegistration', { content_name: `signup - ${authType}` });
    }

    // TikTok Pixel
    if (typeof ttq !== 'undefined') {
      ttq.identify({
        external_id: user.id
      });

      ttq.track('CompleteRegistration');
    }
  } else {
    // TODO: require as argument + pass-in user.id to logLoginSuccess instead of setting outside
    getAmplitudeInstance().setUserId(user?.id);
    if (isCollabListInvite) {
      logLogInSuccess({ authentication_type: authType, deferred_deep_link_type: DeferredDeepLinkType.CollaborativeList });
    } else {
      logLogInSuccess({ authentication_type: authType });
    }
  }
};

const emailSignupHandler = (
  // API params
  // required =>  :first_name, :last_name, :email, :password
  // optional => :create_session, :subscribed, :recaptcha
  params: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    create_session?: boolean;
    subscribed?: boolean;
    recaptcha?: string;
    referral_code?: string;
  },
  locale?: string,
  successCallback: Function = (data: any) => {},
  errorCallback = (err: LegacyAPIError) => {},
  completeCallback = (data: any) => {}
) => {
  const headers = locale ? { 'X-Language-Locale': locale } : {};
  ServerCommunicationUtil.postApiEndpoint(
    '/api/alltrails/register',
    params,
    (data: any) => {
      authSuccessCallback(data, AuthenticationType.Email, data.is_collab_list_invite);
      successCallback(data);
    },
    (err: LegacyAPIError) => {
      const errorMessage = err?.responseJSON?.errors?.[0]?.code || 'unknown';
      logSignUpFailed({ authentication_type: AuthenticationType.Email, failure_type: errorMessage });
      errorCallback(err);
    },
    completeCallback,
    headers
  );
};

const emailLoginHandler = (
  params: {
    email: string;
    password: string;
    create_session?: boolean;
    recaptcha?: string;
  },
  locale?: string,
  successCallback: Function = (data: any) => {},
  errorCallback = (err: LegacyAPIError) => {},
  completeCallback = (data: any) => {}
) => {
  const headers = locale ? { 'X-Language-Locale': locale } : {};
  ServerCommunicationUtil.postApiEndpoint(
    '/api/alltrails/login',
    params,
    (data: any) => {
      authSuccessCallback(data, AuthenticationType.Email, data.is_collab_list_invite);
      successCallback(data);
    },
    (err: LegacyAPIError) => {
      const errorMessage = err?.responseJSON?.errors?.[0]?.code || 'unknown';
      logLogInFailed({ authentication_type: AuthenticationType.Email, failure_type: errorMessage });
      errorCallback(err);
    },
    completeCallback,
    headers
  );
};

export { emailSignupHandler, emailLoginHandler, authSuccessCallback };
